import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosAgent from '../api/AxiosAgent';
import api from '../api/api';
import { UserTypes } from '../constants/UserTypes';
import { getAuthenticatedUserType, isTokenValid } from '../utils/Utils';

interface ProSubscriptionContextValue {
    /** True if the RCIC currently has active Pro access. */
    hasProAccess: boolean;
    /** True while the initial check is in progress. */
    isLoading: boolean;
    /** Re-fetch Pro access status (call after subscribing). */
    refresh: () => Promise<void>;
}

const defaultValue: ProSubscriptionContextValue = {
    hasProAccess: false,
    isLoading: false,
    refresh: async () => {},
};

const ProSubscriptionContext = createContext<ProSubscriptionContextValue>(defaultValue);

export const useProSubscription = (): ProSubscriptionContextValue => useContext(ProSubscriptionContext);

/**
 * ProSubscriptionProvider
 *
 * Fetches and provides Pro subscription access status for authenticated RCIC users.
 * Non-RCIC users always receive hasProAccess = false without making any API calls.
 *
 * Result is cached in localStorage (PRO_ACCESS) to avoid flash-of-locked-content
 * on page reload. The backend is the source of truth — localStorage is only a hint.
 */
export const ProSubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [hasProAccess, setHasProAccess] = useState<boolean>(
        () => localStorage.getItem('PRO_ACCESS') === 'true'
    );
    const [isLoading, setIsLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (!isTokenValid()) return;
        if (getAuthenticatedUserType() !== UserTypes.RCIC) {
            setHasProAccess(false);
            return;
        }
        setIsLoading(true);
        try {
            const resp = await axiosAgent.requests.get(`${api.PRO_SUBSCRIPTION}/access-check`);
            const access = resp?.data?.hasProAccess === true;
            setHasProAccess(access);
            localStorage.setItem('PRO_ACCESS', access ? 'true' : 'false');
        } catch {
            // Keep current cached value on error
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();

        // Re-check after: subscription success, login navigation, or returning to the tab.
        const handler = () => void refresh();
        window.addEventListener('pro-access-updated', handler);

        // Safeguard for OAuth flows where the user returns from a new tab/window,
        // and for cases where the token is set asynchronously after the initial mount.
        const focusHandler = () => {
            if (isTokenValid() && getAuthenticatedUserType() === UserTypes.RCIC) {
                void refresh();
            }
        };
        window.addEventListener('focus', focusHandler);
        // Held in a named reference so cleanup can actually remove it; an inline arrow
        // here left one listener behind on every re-run of this effect.
        const visibilityHandler = () => {
            if (document.visibilityState === 'visible') focusHandler();
        };
        document.addEventListener('visibilitychange', visibilityHandler);

        return () => {
            window.removeEventListener('pro-access-updated', handler);
            window.removeEventListener('focus', focusHandler);
            document.removeEventListener('visibilitychange', visibilityHandler);
        };
    }, [refresh]);

    return (
        <ProSubscriptionContext.Provider value={{ hasProAccess, isLoading, refresh }}>
            {children}
        </ProSubscriptionContext.Provider>
    );
};
